(() => {
  if (customElements.get("scene-solutions")) return;

  class SceneSolutions extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.opener = null;
      this._activeModal = null;
      this._focusTrap = null;
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll("[data-scene-card]"));
      this.modals = Array.from(this.querySelectorAll("[data-scene-modal]"));
      this.bundleButtons = Array.from(
        this.querySelectorAll("[data-bundle-add]"),
      );
      this.bindEvents();
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    setBundleButtonState(button, state) {
      if (!button) return;
      const states = Array.from(
        button.querySelectorAll("[data-bundle-state]")
      );
      if (!states.length) return;
      states.forEach((node) => {
        const isActive = node.dataset.bundleState === state;
        node.classList.toggle("hidden", !isActive);
        if (node.dataset.bundleState === "loading") {
          node.classList.toggle("flex", isActive);
        }
      });
    }

    closeAll() {
      // Remove focus trap from active modal
      if (this._focusTrap && this._activeModal) {
        this._activeModal.removeEventListener("keydown", this._focusTrap);
      }
      this._activeModal = null;
      this._focusTrap = null;

      this.modals.forEach((modal) => {
        const overlay = modal.querySelector("[data-scene-overlay]");
        modal.classList.remove("flex");
        modal.classList.add("hidden");
        modal.classList.remove("opacity-100", "pointer-events-auto");
        modal.classList.add("opacity-0", "pointer-events-none");
        modal.setAttribute("aria-hidden", "true");
        if (overlay) {
          overlay.classList.remove("opacity-100");
          overlay.classList.add("opacity-0");
        }
      });
      document.documentElement.classList.remove("overflow-hidden");

      // Return focus to opener
      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }
    }

    openModal(id, opener) {
      const modal = this.querySelector(`[data-scene-modal="${id}"]`);
      if (!modal) return;
      const overlay = modal.querySelector("[data-scene-overlay]");

      this.closeAll();

      // Store opener for focus return (after closeAll to avoid being cleared)
      this.opener = opener || null;
      modal.classList.remove("hidden");
      modal.classList.add("flex");
      modal.classList.remove("opacity-0", "pointer-events-none");
      modal.classList.add("opacity-100", "pointer-events-auto");
      modal.setAttribute("aria-hidden", "false");
      if (overlay) {
        overlay.classList.remove("opacity-0");
        overlay.classList.add("opacity-100");
      }
      document.documentElement.classList.add("overflow-hidden");

      // Focus trap with Escape handling
      this._activeModal = modal;
      this._focusTrap = webvista.createFocusTrap(modal, {
        onEscape: () => this.closeAll(),
      });
      modal.addEventListener("keydown", this._focusTrap);

      // Auto-focus first element
      webvista.focusFirstElement(modal);
    }

    bindEvents() {
      this.cards.forEach((card) => {
        this.addListener(card, "click", () => {
          const id = card.getAttribute("data-scene-card");
          if (!id) return;
          this.openModal(id, card);
        });

        // Enter / Space to open modal
        this.addListener(card, "keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          const id = card.getAttribute("data-scene-card");
          if (!id) return;
          this.openModal(id, card);
        });
      });

      this.modals.forEach((modal) => {
        modal.querySelectorAll("[data-scene-close]").forEach((el) => {
          this.addListener(el, "click", () => this.closeAll());
        });
      });

      // Bundle buttons clicked -- add all variants to cart
      this.bundleButtons.forEach((button) => {
        this.addListener(button, "click", () => this.addBundleToCart(button));
      });
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId = drawer?.dataset?.sectionId || "cart-drawer";
      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
        { id: "Cart-Drawer", section: drawerSectionId },
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;
        const container = document.getElementById(section.id);
        if (!container) return;
        if (section.id === "Cart-Drawer") {
          const parsed = new DOMParser().parseFromString(html, "text/html");
          const content = parsed.querySelector("[data-cart-drawer-content]");
          const summary = parsed.querySelector("[data-cart-drawer-summary]");
          const contentTarget = container.querySelector(
            "[data-cart-drawer-content]",
          );
          const summaryTarget = container.querySelector(
            "[data-cart-drawer-summary]",
          );
          const progressTarget = container.querySelector(
            "free-shipping-progress",
          );
          if (content && contentTarget) {
            contentTarget.innerHTML = content.innerHTML;
            if (window.webvista?.initLazyImageAnimation) {
              window.webvista.initLazyImageAnimation(contentTarget);
            }
          }
          if (summary && summaryTarget) {
            summaryTarget.innerHTML = summary.innerHTML;
            summaryTarget.className = summary.className;
          }
          const progressSource = parsed.querySelector("free-shipping-progress");
          if (progressTarget && progressSource) {
            progressTarget.dataset.totalAmount =
              progressSource.dataset.totalAmount || "";
          }
          if (typeof container.refreshUpsell === "function") {
            container.refreshUpsell();
          }
          return;
        }
        if (section.selector) {
          const parsed = new DOMParser().parseFromString(html, "text/html");
          const element = parsed.querySelector(section.selector);
          if (element) {
            container.innerHTML = element.innerHTML;
            return;
          }
        }
        container.innerHTML = html;
      });
    }

    // Add bundle variants to cart
    async addBundleToCart(button) {
      const raw = button?.dataset?.bundleVariants || "";
      const variants = raw
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      if (!variants.length) return;

      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.setAttribute("aria-disabled", "true");
      this.setBundleButtonState(button, "loading");

      const formData = new FormData();
      variants.forEach((variantId) => {
        formData.append("items[][id]", variantId);
        formData.append("items[][quantity]", "1");
      });

      const sections = this.getSectionsToRender();
      if (sections.length) {
        formData.append(
          "sections",
          sections.map((section) => section.section).join(","),
        );
      }
      formData.append("sections_url", window.location.pathname);

      const config = window.webvista?.fetchConfig
        ? window.webvista.fetchConfig("javascript")
        : { method: "POST", headers: {} };
      config.method = "POST";
      config.headers = config.headers || {};
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = formData;

      try {
        const response = await fetch("/cart/add.js", config);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data?.sections) {
          this.renderSections(data.sections);
        }
        const returnFocus = this.opener;
        this.closeAll();
        const cartDrawer = document.getElementById("Cart-Drawer");
        if (cartDrawer && typeof cartDrawer.show === "function") {
          cartDrawer.show(returnFocus);
        } else {
          window.location.href = "/cart";
        }
      } catch (error) {
      } finally {
        button.disabled = false;
        button.setAttribute("aria-busy", "false");
        button.setAttribute("aria-disabled", "false");
        this.setBundleButtonState(button, "idle");
      }
    }
  }

  customElements.define("scene-solutions", SceneSolutions);
})();
